import React from "react"
import ReactApexChart from "react-apexcharts"
import { formatDateForKPIDashboard } from "../../../utils/generalUtils"

const LineGraph = ({
	seriesData,
	categories,
	chartData = null,
	color,
	height = 350,
	componentHeight = null,
	tooltipText = "",
	tooltipFormatter = value => value,
	yAxisFormatter = value => Math.round(value),
	minValue,
	maxValue,
	tickAmount = 5,
	// New props for title section
	value,
	valueUnit = "",
	subtitle,
	growthRate,
	// New props for color calculation
	goal,
	isHigherBetter = true,
	thresholdPercentage = 0.8,
	hideTitleSection = false
}) => {
	// Support chartData object if provided instead of separate parameters
	const finalSeriesData = chartData?.series || seriesData
	const finalCategories = chartData?.categories || categories

	// Get min and max values if not provided
	const dataMinValue = typeof minValue !== "undefined" ? minValue : Math.min(...finalSeriesData)
	const dataMaxValue = typeof maxValue !== "undefined" ? maxValue : Math.max(...finalSeriesData)

	const getColorForValue = (value, goal, isHigherBetter, threshold) => {
		const valueNum = parseFloat(value)
		const goalNum = parseFloat(goal)

		if (isHigherBetter) {
			if (valueNum >= goalNum) return "#352D66"
			if (valueNum >= goalNum * threshold) return "#6955EA"
			return "#AEA2FF"
		} else {
			if (valueNum <= goalNum) return "#352D66"
			if (valueNum <= goalNum * (1 + (1 - threshold))) return "#6955EA"
			return "#AEA2FF"
		}
	}

	const getStatusTag = () => {
		const valueNum = parseFloat(value)
		const goalNum = parseFloat(goal)

		if (isHigherBetter) {
			if (valueNum >= goalNum) {
				return (
					<span className="px-2 py-1 bg-green-400 text-white rounded-md text-sm font-medium">
						✅ Exceeding Goal
					</span>
				)
			} else if (valueNum >= goalNum * 0.75) {
				return (
					<span className="px-2 py-1 bg-yellow-300 text-white rounded-md text-sm font-medium">
						⚡ Approaching Target
					</span>
				)
			} else {
				return (
					<span className="px-2 py-1 bg-orange-400 text-white rounded-md text-sm font-medium">
						🔥 Improvement Needed
					</span>
				)
			}
		} else {
			if (valueNum <= goalNum) {
				return (
					<span className="px-2 py-1 bg-green-400 text-white rounded-md text-sm font-medium">
						✅ Exceeding Goal
					</span>
				)
			} else if (valueNum <= goalNum * 1.25) {
				return (
					<span className="px-2 py-1 bg-yellow-400 text-white rounded-md text-sm font-medium">
						⚡ Approaching Target
					</span>
				)
			} else {
				return (
					<span className="px-2 py-1 bg-orange-400 text-white rounded-md text-sm font-medium">
						🔥 Improvement Needed
					</span>
				)
			}
		}
	}

	const displayColor = color || getColorForValue(value, goal, isHigherBetter, thresholdPercentage)

	// Adjust the min and max to nearest multiple of 5 for a cleaner axis
	const adjustedMin = Math.floor(dataMinValue / 5) * 5
	const adjustedMax = Math.ceil(dataMaxValue / 5) * 5

	const series = [
		{
			name: tooltipText || "Value",
			data: finalSeriesData
		}
	]

	const options = {
		chart: {
			height: "100%",
			width: "100%",
			type: "area",
			toolbar: {
				show: false
			},
			fontFamily: "Manrope, sans-serif"
		},
		dataLabels: {
			enabled: false
		},
		stroke: {
			curve: "smooth",
			width: 4,
			colors: [displayColor]
		},
		fill: {
			type: "gradient",
			gradient: {
				shadeIntensity: 1,
				opacityFrom: 0.2,
				opacityTo: 0,
				stops: [0, 100],
				colorStops: [
					{
						offset: 0,
						color: displayColor,
						opacity: 0.2
					},
					{
						offset: 100,
						color: "#FFFFFF",
						opacity: 1
					}
				]
			}
		},
		xaxis: {
			type: "category",
			categories: finalCategories.map(date => formatDateForKPIDashboard(date)),
			title: {
				show: false
			},
			tickAmount: 3,
			offsetX: 30,
			labels: {
				show: true,
				style: {
					fontSize: "12px",
					colors: ["#8c8c8c"]
				},
				rotate: 0
			},
			axisBorder: {
				show: false
			},
			axisTicks: {
				show: false
			}
		},
		yaxis: {
			title: {
				show: false
			},
			labels: {
				show: true,
				formatter: yAxisFormatter,
				style: {
					fontSize: "12px",
					colors: ["#8c8c8c"]
				}
			},
			axisBorder: {
				show: false
			},
			axisTicks: {
				show: false
			},
			tickAmount: tickAmount,
			min: adjustedMin,
			max: adjustedMax
		},
		grid: {
			show: false
		},
		markers: {
			colors: [displayColor],
			strokeColors: "#fff"
		},
		tooltip: {
			marker: {
				show: true,
				fillColors: [displayColor]
			},
			y: {
				formatter: tooltipFormatter
			}
		}
	}

	// Add container style if componentHeight is provided
	const containerStyle = componentHeight ? { height: `${componentHeight}px` } : {}

	return (
		<div style={containerStyle} className="flex flex-col h-full w-full">
			{/* Title Section - only render if hideTitleSection is false and we have value and subtitle */}
			{!hideTitleSection && value && subtitle && (
				<div className="flex justify-between items-center">
					<div>
						<h5 className="text-3xl font-bold text-gray-900">
							{value}
							{valueUnit}
						</h5>
						<p className="text-gray-500 text-base">{subtitle}</p>
					</div>
					<div className="flex items-center">{getStatusTag()}</div>
				</div>
			)}

			{/* Chart Section */}
			<div className="flex-grow">
				<ReactApexChart
					options={options}
					series={series}
					type="area"
					height={height}
					width="100%"
				/>
			</div>
		</div>
	)
}

export default LineGraph
